<template>
  <div v-if="!hideBannerTemporarily">
    <!-- Banner on initial load -->
    <div class="remittance-wrapper blue-border" v-if="isBannerVisible">
      <div class="remittance-container removeRightPadding">
        <template>
          <div class="card-title">
            {{ $t('invoices.add.form.remittance.newUserBanner.title') }}
          </div>
          <div class="card-subtext">
            {{ $t('invoices.add.form.remittance.newUserBanner.subtitle') }}
          </div>
          <div class="transaction-section">
            <div class="transaction-row">
              <div class="f-12">
                {{ $t('invoices.add.form.remittance.transactionAmount') }}
              </div>
              <div class="font-weight-bold f-12 color-noble-blue">
                $ {{ Number(invoiceInformation.maxAmount).toLocaleString() }}
              </div>
            </div>
            <div class="transaction-row">
              <div class="f-12 d-flex align-items-center">
                {{ $t('invoices.add.form.remittance.liveConversionRate') }}
              </div>
              <div
                v-if="!dripRates && isRateConversionApiFulfilled"
                class="rate-fail-wrapper"
              >
                <b-icon-exclamation-triangle class="fail-icon" />
                {{
                  $t(
                    'dashboard.conversionRateDetailsWidget.rateConversionRateFail'
                  )
                }}
              </div>
              <div class="font-weight-bold blue-border" v-else>
                ₹ {{ getDripRates }}
              </div>
            </div>
            <div class="transaction-row">
              <div class="f-12">
                {{ $t('invoices.add.form.remittance.addedFees') }}
              </div>
              <div class="font-weight-bold f-12 color-noble-blue">$0</div>
            </div>
            <hr class="grey-divider" />
            <div class="transaction-row">
              <div class="f-12">
                {{ $t('invoices.add.form.remittance.receiveINR') }}
              </div>
              <div class="font-weight-bold color-noble-blue">
                {{ getAmountInINR }}
              </div>
            </div>
          </div>
          <div class="d-flex">
            <action-button
              variant="outline"
              :click="openEnableDripSwitchModalOpen"
              class="mr-2"
            >
              {{ $t('invoices.add.form.remittance.newUserBanner.inrButton') }}
            </action-button>
            <action-button
              variant="text"
              :click="openDisableDripSwitchModalOpen"
            >
              {{ $t('invoices.add.form.remittance.newUserBanner.denyButton') }}
            </action-button>
          </div>
          <div class="d-flex align-items-center my-3">
            <b-icon-info-circle class="info-icon" />
            <div class="info-text line-height-1">
              {{ $t('invoices.add.form.remittance.info') }}
            </div>
          </div>
          <enable-drip-switch-modal
            :refId="invoiceInformation.reference"
            :invoiceId="invoice_id"
            :invoiceLevel="false"
            :isEnableDripSwitchOpen="isEnableDripSwitchModalOpen"
            :dataToSend="getEnableDripSwitchApiData"
            :onClose="onModalClose"
            :onCancel="onModalCancel"
            :onSuccess="onStatusToggleSuccess"
          />
          <disable-drip-switch-sidebar
            :alternate="true"
            :isDisableDripSwitchOpen="isDisableDripSwitchDrawerOpen"
            :dataToSend="getDisableDripSwitchApiData"
            :onCancel="onDrawerCancel"
            :onClose="onDrawerClose"
            :onSuccess="onStatusToggleSuccess"
          />
        </template>
      </div>
      <div>
        <img
          v-if="!$store.state.root.isInrTransactionCompleted"
          src="@/assets/images/common/invoice-inr-payment.svg"
          class="widget-background-image"
          width="320"
        />
        <img
          v-else
          src="@/assets/images/common/invoice-document-vertical.svg"
          class="widget-background-image document-image"
          width="320"
        />
      </div>
    </div>
    <!-- After the first switch -->
    <div
      v-else-if="showRemittanceBanner"
      class="remittance-wrapper"
      :class="
        invoiceInformation.isDripSwitchEnabled ? 'blue-border' : 'orange-border'
      "
    >
      <div class="remittance-container w-100">
        <template>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="card-heading">
                {{ $t('invoices.add.form.remittance.heading') }}
              </div>
              <remittance-flag-chip :isEnabaled="isRemittanceEnabled" />
            </div>
            <div class="d-flex align-items-center">
              <toggle-switch
                :value="isRemittanceEnabled"
                :onToggle="onSwitchChange"
              />
            </div>
          </div>
          <div class="info-text mt-2">
            {{
              isRemittanceEnabled
                ? $t('invoices.add.form.remittance.infoTextEnabled')
                : $t('invoices.add.form.remittance.infoTextDisabled')
            }}
          </div>
          <enable-drip-switch-modal
            :refId="invoiceInformation.reference"
            :invoiceId="invoice_id"
            :invoiceLevel="true"
            :isEnableDripSwitchOpen="isEnableDripSwitchModalOpen"
            :dataToSend="getEnableDripSwitchApiData"
            :onClose="onModalClose"
            :onCancel="onModalCancel"
            :onSuccess="onSuccessInvoiceSwitch"
          />
          <disable-drip-switch-sidebar
            :isDisableDripSwitchOpen="isDisableDripSwitchDrawerOpen"
            :dataToSend="getDisableDripSwitchApiData"
            :onCancel="onDrawerCancel"
            :onClose="onDrawerClose"
            :onSuccess="onSuccessInvoiceSwitch"
          />
        </template>
      </div>
    </div>
    <enabled-message-modal
      :userLevel="isUniversal"
      :showDialog="showEnabledMessageModal"
      @closeDialog="onCloseEMMDialog"
    />
  </div>
</template>
<script>
import moment from 'moment'
import EnableDripSwitchModal from '@/components/drip-switch/EnableDripSwitchModal'
import DisableDripSwitchSidebar from '@/components/drip-switch/DisableDripSwitchSidebar'
import { currencyConverter } from '@/utils/common'
import {
  RATE_CONVERSION_REFETCH_DEFAULT_TIME,
  MAXIMUM_SET_TIMEOUT_DELAY
} from '@/utils/constants'
import RemittanceFlagChip from '@/components/common/RemittanceFlagChip'
import conversionRateService from '@/services/conversionRate'
import exportersService from '@/services/exporters'
import ToggleSwitch from '@/components/common/Switch'
import ActionButton from '@/components/common/ActionButton'
import EnabledMessageModal from '@/components/drip-switch/EnabledMessageModal'

export default {
  props: [
    'invoice_id',
    'invoiceInformation',
    'onStatusToggleSuccess',
    'isInrTransactionCompleted'
  ],
  data() {
    return {
      dripRates: null,
      isEnableDripSwitchModalOpen: false,
      isDisableDripSwitchDrawerOpen: false,
      isRateConversionApiFulfilled: false,
      timeOutID: '',
      showEnabledMessageModal: false,
      isUniversal: false,
      hideBannerTemporarily: false
    }
  },
  components: {
    EnableDripSwitchModal,
    DisableDripSwitchSidebar,
    RemittanceFlagChip,
    ActionButton,
    ToggleSwitch,
    EnabledMessageModal
  },
  methods: {
    refetchUserRemittance() {
      exportersService
        .fetchExporterMetaData(this.$store.state.account.accountId)
        .then((res) => {
          this.$store.dispatch(
            'setDripSwitchUserLevel',
            res.data.drip_switch_enabled
          )
        })
        .catch((err) => {
          console.log(err)
        })
    },
    onSuccessEnabledSwitch() {
      this.showEnabledMessageModal = true
    },
    onSuccessInvoiceSwitch() {
      this.refetchUserRemittance()
      this.onStatusToggleSuccess()
    },
    onCloseEMMDialog() {
      this.refetchUserRemittance()
      this.showEnabledMessageModal = false
    },
    async fetchConversionRate() {
      this.isRateConversionApiFulfilled = false
      const exporterId = this.findExporter(
        this.$store.state.account.accountId
      ).id

      try {
        const { data } = await conversionRateService.getConversionRate(
          exporterId
        )
        this.isRateConversionApiFulfilled = true
        this.dripRates = data
        let timeLeft = 0
        timeLeft = moment(this.dripRates.valid_till).diff(new Date())
        let bufferAddedToTimeLeft = timeLeft + 100 // 100ms buffer added before making another API call so that we always fetch the newer data
        if (
          !bufferAddedToTimeLeft ||
          bufferAddedToTimeLeft < 0 ||
          bufferAddedToTimeLeft > MAXIMUM_SET_TIMEOUT_DELAY
        ) {
          bufferAddedToTimeLeft = RATE_CONVERSION_REFETCH_DEFAULT_TIME
        }
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.fetchConversionRate()
        }, bufferAddedToTimeLeft)
      } catch (err) {
        this.isRateConversionApiFulfilled = true
        clearTimeout(this.timeOutID)
        this.timeOutID = setTimeout(() => {
          this.fetchConversionRate()
        }, RATE_CONVERSION_REFETCH_DEFAULT_TIME)
      }
    },
    onSwitchChange() {
      if (this.isRemittanceEnabled) {
        this.$mpTrack('Clicks disable for INR while submitting an invoice', {
          invoice_id: this.invoice_id,
          banner: this.isBannerVisible
        })
        this.isDisableDripSwitchDrawerOpen = true
      } else {
        this.$mpTrack('Clicks enable for INR while submitting an invoice', {
          invoice_id: this.invoice_id,
          banner: this.isBannerVisible
        })
        this.isEnableDripSwitchModalOpen = true
      }
    },
    openEnableDripSwitchModalOpen() {
      this.$mpTrack('Clicks enable for INR while submitting an invoice', {
        invoice_id: this.invoice_id,
        banner: this.isBannerVisible
      })
      this.isEnableDripSwitchModalOpen = true
    },
    openDisableDripSwitchModalOpen() {
      this.$mpTrack('Clicks disable for INR while submitting an invoice', {
        invoice_id: this.invoice_id,
        banner: this.isBannerVisible
      })
      this.isDisableDripSwitchDrawerOpen = true
    },
    onModalCancel() {
      this.isEnableDripSwitchModalOpen = false
    },
    onModalClose(switchFor) {
      if (switchFor && switchFor === 'user') {
        this.isUniversal = true
        this.showEnabledMessageModal = true
      }
      if (switchFor && switchFor === 'invoice') {
        this.isUniversal = false
        this.$notify({
          title: this.$t(
            'dashboard.enableDripSwitchModal.successMessage.title'
          ),
          type: 'success'
        })
      }
      this.isEnableDripSwitchModalOpen = false
    },
    onDrawerClose() {
      this.isDisableDripSwitchDrawerOpen = false
      if (this.isBannerVisible) {
        this.$mpTrack('Add Invoice - Not interested btn banner triggered')
        this.hideBannerTemporarily = true
      } else {
        this.$mpTrack('Add Invoice - Disable DRIP Switch for invoice level')
        this.$notify({
          title: this.$t(
            'dashboard.disableDripSwitchSidebar.successMessage.title'
          ),
          type: 'success'
        })
        this.refetchUserRemittance()
      }
    },
    onDrawerCancel() {
      this.isDisableDripSwitchDrawerOpen = false
    },
    findExporter(exporterId) {
      if (this.$store.getters.linked_users) {
        return this.$store.getters.linked_users.find((exporter) => {
          return parseInt(exporter.id) === parseInt(exporterId)
        })
      }
      return {}
    }
  },
  mounted() {
    this.fetchConversionRate()
  },
  beforeDestroy() {
    clearTimeout(this.timeOutID)
  },
  computed: {
    isBannerVisible() {
      return (
        !this.$store.state.account.account.drip_switch_enabled &&
        !this.invoiceInformation.isDripSwitchEnabled
      )
    },
    getAmountInINR() {
      if (this.dripRates && this.isRateConversionApiFulfilled) {
        const transactionAmount = this.invoiceInformation.maxAmount
        const conversionRate = this.dripRates.rate
        return `₹ ${currencyConverter(conversionRate, transactionAmount)}`
      } else {
        return '-'
      }
    },
    getEnableDripSwitchApiData() {
      return {
        invoice_id: this.invoice_id,
        user_id: this.$store.state.account.accountId,
        enabled: true
      }
    },
    getDisableDripSwitchApiData() {
      return {
        invoice_id: this.invoice_id,
        user_id: this.$store.state.account.accountId,
        enabled: false
      }
    },
    isRemittanceEnabled() {
      return this.invoiceInformation.isDripSwitchEnabled
    },
    showRemittanceBanner() {
      return !this.$store.state.account.account.drip_switch_enabled
    },
    getDripRates() {
      if (this.dripRates) {
        return this.dripRates.rate
      }
      return '-'
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/_palette.scss';

.blue-border {
  border-color: $sky-blue-400;
}
.orange-border {
  border-color: $mustard-yellow-400;
}
.remittance-wrapper {
  display: flex;
  justify-content: space-between;
  border-radius: 0.5rem;
  font-family: $font-family-sans-serif;
  border-width: 2px;
  border-style: solid;
  overflow: hidden;
  .remittance-container {
    padding: 32px 24px;
    &.removeRightPadding {
      padding-right: 0;
    }
  }
  .line-height-1 {
    line-height: 1rem;
  }
  .card-heading {
    font-weight: bold;
    color: map-get($noble-blue, 500);
    font-size: 1.25rem;
  }
  .card-title {
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    margin-bottom: 8px;
    color: $noble-blue-500;
  }
  .card-subtext {
    font-weight: 700;
    font-size: 16px;
    line-height: 19px;
    margin-bottom: 24px;
    color: $noble-blue-500;
  }
  .widget-background-image {
    object-fit: cover;
    object-position: right;
    height: 100%;
    &.document-image {
      object-fit: fill;
      padding: 40px 56px;
    }
  }
  .color-noble-blue {
    color: $noble-blue-500;
  }
  .transaction-section {
    width: 80%;
    margin-top: 1rem;
    .transaction-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      padding-right: 10%;
      .rate-fail-wrapper {
        border: 1px solid map-get($mustard-yellow, 300);
        color: map-get($noble-blue, 500);
        font-size: 0.75rem;
        padding: 0.25rem;
        border-radius: 0.25rem;
        margin-left: 2rem;
        line-height: 1;
      }
      .fail-icon {
        margin-right: 0.25rem;
        color: map-get($mustard-yellow, 500);
      }
      .f-12 {
        font-size: 0.8rem;
      }
      .blue-border {
        border: 1px solid map-get($sky-blue, 300);
        border-radius: 0.5rem;
        padding: 0.2rem 0.5rem;
      }
    }
    .grey-divider {
      color: map-get($neutral-grey, 500);
      margin: 0.5rem 0;
    }
  }
  .switch-label {
    font-size: 14px;
    font-weight: bold;
    margin-right: 0.5rem;
  }
  .info-text {
    font-size: 0.75rem;
  }
  .info-icon {
    margin-right: 0.5rem;
    color: map-get($noble-blue, 300);
    font-size: 0.8rem;
  }
}
</style>
